import { supabase } from './supabase';
import type {
  Baby,
  Profile,
  Session,
  Message,
  CreateBabyRequest,
  DailyContentResponse,
  DetectCountryResponse,
  SubscribePushRequest,
  UpdateMotherProfileRequest,
} from '@/types';
import type { JourneyResponse, TimelineResponse, CardState } from '@/types/journey';
import { isGerman } from '@/i18n/locale';

function langParam(): string {
  return isGerman() ? '&lang=de' : '';
}

// ════════════════════════════════════════════════════════════════════
// Auth helper — pulls fresh access token for each request.
// ════════════════════════════════════════════════════════════════════

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function jsonRequest<T>(
  url: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = {
    'Content-Type': 'application/json',
    ...(await authHeaders()),
    ...(init.headers ?? {}),
  };
  const res = await fetch(url, { ...init, headers });
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    throw new ApiError(
      typeof data === 'object' && data && 'error' in data ? String(data.error) : 'request_failed',
      res.status,
      typeof data === 'object' && data && 'code' in data ? String(data.code) : undefined,
      data
    );
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ════════════════════════════════════════════════════════════════════
// Profile
// ════════════════════════════════════════════════════════════════════

export interface ProfileResponse {
  profile: Profile;
  usage: { used: number; limit: number | null; token_balance: number };
}

export const api = {
  profile: {
    get: () => jsonRequest<ProfileResponse>('/api/profile'),
    updateMother: (body: UpdateMotherProfileRequest) =>
      jsonRequest<{ profile: Profile }>('/api/profile', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    markNutritionSetupComplete: () =>
      jsonRequest<{ profile: Profile }>('/api/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          nutrition_setup_completed_at: new Date().toISOString(),
        }),
      }),
    detectCountry: () =>
      jsonRequest<DetectCountryResponse>('/api/profile/detect-country', {
        method: 'POST',
      }),
  },
  babies: {
    list: () => jsonRequest<{ babies: Baby[] }>('/api/babies'),
    create: (body: CreateBabyRequest) =>
      jsonRequest<{ baby: Baby }>('/api/babies', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: Partial<CreateBabyRequest>) =>
      jsonRequest<{ baby: Baby }>(`/api/babies?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      jsonRequest<void>(`/api/babies?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
  },
  sessions: {
    list: () => jsonRequest<{ sessions: Session[] }>('/api/sessions'),
    get: (sessionId: string) =>
      jsonRequest<{ session: Session; messages: Message[] }>(
        `/api/sessions?session_id=${encodeURIComponent(sessionId)}`
      ),
  },
  dailyContent: {
    get: (babyId: string) =>
      jsonRequest<DailyContentResponse>(
        `/api/daily-content?baby_id=${encodeURIComponent(babyId)}${langParam()}`
      ),
  },
  notifications: {
    subscribe: (body: SubscribePushRequest) =>
      jsonRequest<{ ok: true }>('/api/notifications/subscribe', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    unsubscribe: (endpoint: string) =>
      jsonRequest<{ ok: true }>('/api/notifications/subscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      }),
  },
  journey: {
    thisWeek: (babyId: string) =>
      jsonRequest<JourneyResponse>(`/api/journey?baby_id=${encodeURIComponent(babyId)}${langParam()}`),
    timeline: (babyId: string) =>
      jsonRequest<TimelineResponse>(
        `/api/journey?baby_id=${encodeURIComponent(babyId)}&view=timeline${langParam()}`
      ),
    log: (cardId: string, babyId: string, action: Exclude<CardState, 'unseen'>, note?: string) =>
      jsonRequest<void>('/api/journey', {
        method: 'POST',
        body: JSON.stringify({ card_id: cardId, baby_id: babyId, action, note }),
      }),
  },
};

export { ApiError, authHeaders };
